import React, { useCallback, useEffect, useState } from "react";
import { Button, Modal, Popconfirm, Spin, Table, message } from "antd";

import * as api from "../controller";
import OfficeDetailDrawer from "../OfficeDetailDrawer";
import TaxLinesEditor from "./TaxLinesEditor";
import { formLines, linesText } from "./taxMath";
import "../leadSettings.css";
import "./billing.css";

const ok = (r) => r && r.status >= 200 && r.status < 300;

/**
 * CRM Settings → Sales tax: the default and every province's taxes, how many
 * offices each one taxes today, the practices and offices taxed differently, and
 * the offices whose province can't be read from their address (they pay the
 * default until it's fixed on their Users & Billing tab).
 *
 * A change applies to new charges. A plan already bought keeps the tax it was
 * bought with, at every renewal.
 */
export default function SalesTaxPanel() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [editing, setEditing] = useState(null); // {code, name, offices, lines}
  const [saving, setSaving] = useState(false);
  const [office, setOffice] = useState(null); // {officeId, practiceId} open in the drawer

  const load = useCallback(async () => {
    const r = await api.getSalesTax();
    setFailed(!ok(r));
    if (ok(r)) setData(r.json);
  }, []);
  useEffect(() => { load(); }, [load]);

  const answer = (r, done) => {
    if (ok(r)) {
      setData(r.json);
      message.success(done);
      return true;
    }
    message.error((r.json && r.json.detail) || "Couldn't save the sales tax.");
    return false;
  };

  const save = async () => {
    setSaving(true);
    const r = await api.saveTaxRegion(editing.code, editing.lines);
    setSaving(false);
    if (answer(r, `${editing.name} sales tax saved. New charges use it now.`)) setEditing(null);
  };

  const remove = async (row) => {
    answer(await api.clearTaxRegion(row.code), `${row.name} now pays the default.`);
  };

  if (failed && !data) {
    return (
      <p className="crm-sub">
        Couldn&rsquo;t load the sales tax.{" "}
        <Button size="small" type="link" style={{ padding: 0 }} onClick={load}>Try again</Button>
      </p>
    );
  }
  if (!data) return <Spin />;

  const unplaced = data.unplaced || [];
  const exceptions = data.exceptions || [];
  const openOffice = (o) => setOffice({ officeId: o.office_id, practiceId: o.practice_id });

  return (
    <div className="leadset-panel">
      <div className="leadset-hint" style={{ margin: "4px 0 10px" }}>
        Each office pays its own rate if it has one, else its practice&rsquo;s, else its province&rsquo;s, else the
        default. Changes apply to new charges; a plan already bought keeps the tax it was bought with.
      </div>
      <Table
        rowKey="code"
        size="small"
        pagination={false}
        dataSource={data.regions || []}
        columns={[
          {
            title: "Where", key: "name",
            render: (_, r) => (r.code === "DEFAULT"
              ? <><strong>Default</strong><div className="leadset-hint">Any office without a province rate</div></>
              : r.name),
          },
          {
            title: "Charged", key: "lines",
            render: (_, r) => (r.code === "DEFAULT" || r.has_rule
              ? linesText(r.lines)
              : <span className="leadset-hint">Uses the default</span>),
          },
          { title: "Offices", dataIndex: "offices", key: "offices", width: 80, align: "right" },
          {
            title: "", key: "actions", width: 150, align: "right",
            render: (_, r) => (
              <span style={{ display: "inline-flex", gap: 6 }}>
                <Button
                  size="small"
                  onClick={() => setEditing({
                    code: r.code, name: r.name, offices: r.offices,
                    lines: r.lines && r.lines.length ? formLines(r.lines) : [{ label: "", percent: "" }],
                  })}
                >
                  {r.code === "DEFAULT" || r.has_rule ? "Edit" : "Set"}
                </Button>
                {r.code !== "DEFAULT" && r.has_rule ? (
                  <Popconfirm title={`Remove the ${r.name} rate? Its offices pay the default.`} okText="Remove"
                              onConfirm={() => remove(r)}>
                    <Button size="small" danger>Remove</Button>
                  </Popconfirm>
                ) : null}
              </span>
            ),
          },
        ]}
      />

      {exceptions.length ? (
        <div className="ub-tax-list">
          <div className="ub-fact-label">Taxed differently</div>
          {exceptions.map((x) => (
            <div key={`${x.scope}-${x.practice_id || x.office_id}`} className="leadset-hint">
              {x.scope === "office" ? (
                <button type="button" className="ub-link" onClick={() => openOffice(x)}>{x.name}</button>
              ) : <strong>{x.name} (whole practice)</strong>}
              {` — ${linesText(x.lines)}. ${x.reason}`}
            </div>
          ))}
        </div>
      ) : null}

      {unplaced.length ? (
        <div className="ub-callout is-warn" style={{ margin: "12px 0 4px" }}>
          We can&rsquo;t tell which province {unplaced.length === 1 ? "this office is" : `these ${unplaced.length} offices are`} in,
          so {unplaced.length === 1 ? "it pays" : "they pay"} the default:{" "}
          {unplaced.map((o, i) => (
            <React.Fragment key={o.office_id}>
              {i ? ", " : ""}
              <button type="button" className="ub-link" onClick={() => openOffice(o)}>
                {o.office_name}
              </button>
              {` (“${o.state || "blank"}”)`}
            </React.Fragment>
          ))}
          . Open each and set its province on the Users &amp; Billing tab.
        </div>
      ) : null}

      <Modal
        open={!!editing}
        title={editing ? `${editing.name} sales tax` : ""}
        okText="Save"
        confirmLoading={saving}
        onOk={save}
        onCancel={() => setEditing(null)}
        destroyOnClose
      >
        {editing ? (
          <>
            {editing.code === "DEFAULT" ? (
              <div className="ub-callout is-warn" style={{ margin: "0 0 12px" }}>
                The default taxes every office without a province, practice or office rate —{" "}
                {editing.offices} office{editing.offices === 1 ? "" : "s"} today.
              </div>
            ) : null}
            <TaxLinesEditor value={editing.lines} onChange={(lines) => setEditing({ ...editing, lines })} />
            <div className="leadset-hint" style={{ marginTop: 10 }}>
              New charges use it as soon as you save. Plans already bought keep the tax they were bought with.
            </div>
          </>
        ) : null}
      </Modal>

      <OfficeDetailDrawer
        officeId={office ? office.officeId : null}
        practiceId={office ? office.practiceId : null}
        initialTab="users"
        onClose={() => { setOffice(null); load(); }}
      />
    </div>
  );
}
